/**
 * Edge types for the Needs Map
 */

import { Status, ErrorInfo, ContentItem } from './nodeTypes';

/**
 * Strength of the connection between nodes
 */
export type EdgeStrength = 'weak' | 'medium' | 'strong';

/**
 * Edge connecting two topic nodes
 */
export interface TopicEdge {
  id: string;
  source: string;
  target: string;
  question: string;
  strength: EdgeStrength;
  extractionPrompt: string;
  contents: ContentItem[]; // Array of content items with timestamps
  content?: string; // Simplified content string for easier handling
  status: Status;
  error: ErrorInfo | null;
  active: boolean; // Whether this edge is active (has content and both connected nodes are active)
}

/**
 * Edge data for ReactFlow
 */
export interface EdgeData {
  question: string;
  strength: EdgeStrength;
  contents: ContentItem[];
  content?: string;
  status: Status;
  error: ErrorInfo | null;
  active: boolean;
  extractionPrompt: string;
}

/**
 * Create a new topic edge
 */
export function createTopicEdge(
  id: string,
  source: string,
  target: string,
  question: string,
  extractionPrompt: string,
  strength: EdgeStrength = 'medium'
): TopicEdge {
  return {
    id,
    source,
    target,
    question,
    strength,
    extractionPrompt,
    contents: [],
    content: '',
    status: 'pending',
    error: null,
    active: false
  };
}

/**
 * Get the edge strength width for visualization
 */
export function getEdgeStrengthWidth(strength: EdgeStrength): number {
  switch (strength) {
    case 'strong':
      return 3;
    case 'medium':
      return 2;
    case 'weak':
      return 1;
    default:
      return 2;
  }
}

/**
 * Get the edge status color for visualization
 */
export function getEdgeStatusColor(status: Status): string {
  switch (status) {
    case 'success':
      return '#10b981'; // green
    case 'error':
      return '#ef4444'; // red
    case 'partial':
      return '#f59e0b'; // amber
    case 'processing':
      return '#6366f1'; // indigo
    default:
      return '#94a3b8'; // slate
  }
}

/**
 * Get the status variant for UI components
 */
export function getStatusVariant(status: Status): 'default' | 'secondary' | 'destructive' | 'success' | 'warning' {
  switch (status) {
    case 'success':
      return 'success';
    case 'error':
      return 'destructive';
    case 'partial':
      return 'warning';
    case 'processing':
      return 'secondary';
    default:
      return 'default';
  }
}

/**
 * Get the status label for display
 */
export function getStatusLabel(status: Status): string {
  switch (status) {
    case 'success':
      return '完了';
    case 'error':
      return 'エラー';
    case 'partial':
      return '部分的';
    case 'processing':
      return '処理中';
    default:
      return '未処理';
  }
}

/**
 * Check if edge is active based on contents and connected nodes
 */
export function isEdgeActive(edge: TopicEdge, sourceNodeActive: boolean, targetNodeActive: boolean): boolean {
  // Edge is active only if both connected nodes are active and it has valid content
  if (!sourceNodeActive || !targetNodeActive) {
    return false;
  }
  
  // Check if edge has valid content
  if (edge.contents.length > 0) {
    return edge.contents.some(item => 
      item.content && 
      item.content.trim() !== '' &&
      !item.content.includes('言及なし') &&
      !item.content.includes('抽出できませんでした')
    );
  } else if (edge.content) {
    // Backward compatibility with content string
    return edge.content.trim() !== '' &&
           !edge.content.includes('言及なし') &&
           !edge.content.includes('抽出できませんでした');
  }
  return false;
}

/**
 * Update edge active status based on contents and connected nodes
 */
export function updateEdgeActiveStatus(edge: TopicEdge, sourceNodeActive: boolean, targetNodeActive: boolean): TopicEdge {
  return {
    ...edge,
    active: isEdgeActive(edge, sourceNodeActive, targetNodeActive)
  };
}